/**
 * Deity acquisition engine — the ONE ladder (Update Directive 18 Oct 2026 §2–3).
 * Pure functions over a deity's completion log + the per-day ledger
 * (gameSettings.ts); nothing here touches the DB or React. Everything is
 * recomputed from history, so a completed rung is recorded once and never
 * re-counted, and an acquired deity never resets.
 *
 * The ladder (all sizes/thresholds user-set, default 3 of 4 at every rung):
 *   Day       `dayThreshold` of the day's `dayActions` required actions done
 *             AND the Day orb done (morning + evening sessions, §6)
 *   Aim       a week holding `aimThreshold` qualifying Days
 *   Goal      a fixed `goalAims`-week block holding `goalThreshold` Aims
 *             (blocks are anchored to the deity's start week, not months)
 *   Acquired  `goalsToAcquire` Goals (≈ one 3-month season)
 *
 * A "required action" is one entry in the deity's principle log for that day
 * (using the tool's method correctly — the log is only written by real reps).
 * Points, seasons and the 400 bar are superseded; the old fields on
 * ProgressStats stay populated (in ladder units) so older readers compile.
 */
import { DEFAULT_GAME_SETTINGS, isDayOrbDone, isWeekBoundaryEvening, weekStartOf, type GameSettings, type PointsLedger } from './gameSettings';

/** Per-deity persisted progress: active principle picks + the completion log,
 *  both keyed by stable principle id (`toolId:levelIdx:principleIdx`). */
export interface DeityProgress {
  /** Active principle ids. */
  picks: string[];
  /** 'YYYY-MM-DD' → principle ids completed that day. */
  log: Record<string, string[]>;
  /** 'YYYY-MM-DD' the deity became active (first pick). Its daily requirement —
   *  and so its seat marks — run from this day. Falls back to the first log day. */
  startedOn?: string;
}

export const EMPTY_PROGRESS: DeityProgress = { picks: [], log: {} };

/** The shared ledger + the user's game rules (from useCompassConfig). */
export interface ProgressContext {
  ledger?: PointsLedger;
  game?: GameSettings;
}

export type LadderRung = 'locked' | 'unlocked' | 'training' | 'acquired';

export interface ProgressStats {
  /** §3 state from the ladder alone (lock state is the caller's: prerequisites). */
  rung: LadderRung;
  /** Qualifying Days / Aims / Goals ever earned (each recorded once). */
  days: number;
  aims: number;
  goals: number;
  /** Today's required actions done, and whether today's Day orb is done. */
  todayActions: number;
  todayOrbDone: boolean;
  /** Qualifying Days in the current week, Aims in the current Goal block. */
  weekDays: number;
  blockAims: number;
  /** One plain line: the next thing this deity needs. */
  next: string;
  /** 0 nothing · 1 a Day · 2 an Aim · 3 a Goal · 4 ACQUIRED — drives the front dots. */
  tierIdx: number;
  /** True once acquired (permanent). */
  secured: boolean;
  /** 0..1 toward acquisition (or toward the next level once acquired). */
  pct: number;
  /** Level: 1 until acquired, +1 per further `goalsToAcquire` Goals (unlocks advanced principles). */
  level: number;
  maxed: boolean;
  /** Principle ids already logged today. */
  doneToday: string[];
  /** Active pick ids for the current level (picks ∩ current-level ids). */
  activePicks: string[];
  /** @deprecated ladder units now (Goals); kept so older readers compile. */
  points: number;
  /** @deprecated = todayActions history total; use `days`. */
  steps: number;
  /** @deprecated = days. */
  todayCount: number;
  /** @deprecated always 0. */
  dayAwards: number;
  /** @deprecated Goals at the start/end of the current level. */
  levelFloor: number;
  levelTarget: number;
  /** @deprecated no pace projection under the ladder. */
  ratePerDay: number;
  projectedDate: string | null;
  seasonEnd: string | null;
}

/** Parse 'YYYY-MM-DD' at local noon. Returns null on malformed input. */
export function parseDay(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
}

export function dayKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MS_DAY = 24 * 3600 * 1000;
const weekKey = (d: Date, game: GameSettings) => dayKeyOf(weekStartOf(d, game));

/** First active day of a deity: its start, else its first logged day. */
export function startDayOf(progress: DeityProgress | undefined): string | null {
  if (progress?.startedOn && parseDay(progress.startedOn)) return progress.startedOn;
  const keys = Object.keys(progress?.log ?? {}).filter((k) => parseDay(k) && (progress!.log[k]?.length ?? 0) > 0).sort();
  return keys[0] ?? null;
}

/** Did this deity's daily requirement get met on `day`? (actions only — the seat
 *  rule; the ladder additionally needs the Day orb). */
export function requirementMet(progress: DeityProgress | undefined, day: string, game: GameSettings = DEFAULT_GAME_SETTINGS): boolean {
  const n = new Set(progress?.log?.[day] ?? []).size;
  return n >= game.ladder.dayThreshold;
}

/**
 * Compute the ladder for one deity from its progress log + the shared ledger.
 * `data.levelIds` = principle ids per level (advanced levels unlock after acquisition).
 */
export function computeProgress(
  progress: DeityProgress | undefined,
  data: { levelIds: string[][]; perWeek?: Record<string, number> },
  todayStr: string,
  _seed = '',
  ctx: ProgressContext = {},
): ProgressStats {
  const game = ctx.game ?? DEFAULT_GAME_SETTINGS;
  const L = game.ladder;
  const ledger = ctx.ledger ?? {};
  const log = progress?.log ?? {};
  const picks = progress?.picks ?? [];
  const today = parseDay(todayStr) ?? new Date();
  const start = startDayOf(progress);
  const engaged = picks.length > 0 || !!start;

  // Qualifying Days: enough distinct required actions AND the Day orb done.
  const qualDays = Object.keys(log)
    .filter((k) => parseDay(k) && parseDay(k)!.getTime() <= today.getTime() + MS_DAY / 2)
    .filter((k) => new Set(log[k]).size >= L.dayThreshold && isDayOrbDone(ledger[k]))
    .sort();

  // Aims: weeks holding `aimThreshold` qualifying Days.
  const daysByWeek = new Map<string, number>();
  for (const k of qualDays) {
    const w = weekKey(parseDay(k)!, game);
    daysByWeek.set(w, (daysByWeek.get(w) ?? 0) + 1);
  }
  const aimWeeks = [...daysByWeek.entries()].filter(([, n]) => n >= L.aimThreshold).map(([w]) => w).sort();

  // Goals: fixed `goalAims`-week blocks from the deity's start week.
  const anchor = weekStartOf(parseDay(start ?? todayStr) ?? today, game);
  const blockOf = (w: string) => Math.floor(Math.round((parseDay(w)!.getTime() - anchor.getTime()) / MS_DAY) / (7 * L.goalAims));
  const aimsByBlock = new Map<number, number>();
  for (const w of aimWeeks) {
    const b = blockOf(w);
    if (b < 0) continue;
    aimsByBlock.set(b, (aimsByBlock.get(b) ?? 0) + 1);
  }
  const goals = [...aimsByBlock.values()].filter((n) => n >= L.goalThreshold).length;

  const secured = goals >= L.goalsToAcquire;
  const levelCount = Math.max(1, data.levelIds.length);
  const level = Math.min(levelCount, 1 + Math.floor(goals / L.goalsToAcquire));
  const maxed = level >= levelCount && secured && goals >= levelCount * L.goalsToAcquire;

  const todayKeyStr = dayKeyOf(today);
  const doneToday = Array.isArray(log[todayKeyStr]) ? log[todayKeyStr] : [];
  const todayActions = new Set(doneToday).size;
  const todayOrbDone = isDayOrbDone(ledger[todayKeyStr]);
  const weekDays = daysByWeek.get(weekKey(today, game)) ?? 0;
  const curBlock = blockOf(weekKey(today, game));
  const blockAims = aimsByBlock.get(curBlock) ?? 0;

  const goalsInLevel = goals - (level - 1) * L.goalsToAcquire;
  const partial = Math.min(1, blockAims / L.goalThreshold) * (blockAims >= L.goalThreshold ? 0 : 1);
  const pct = maxed ? 1 : Math.max(0, Math.min(1, (goalsInLevel + partial) / L.goalsToAcquire));

  const tierIdx = secured ? 4 : goals > 0 ? 3 : aimWeeks.length > 0 ? 2 : qualDays.length > 0 ? 1 : 0;
  const rung: LadderRung = secured ? 'acquired' : qualDays.length > 0 || todayActions > 0 ? 'training' : 'unlocked';

  let next: string;
  if (todayActions < L.dayThreshold) next = `Today: ${todayActions} of ${L.dayThreshold} actions`;
  else if (!todayOrbDone) next = 'Today: finish the Day orb (morning + evening)';
  else if (weekDays < L.aimThreshold) next = `This week: ${weekDays} of ${L.aimThreshold} Days`;
  else if (blockAims < L.goalThreshold) next = `This block: ${blockAims} of ${L.goalThreshold} Aims`;
  else next = secured ? 'Acquired · hold the seat' : `${goals} of ${L.goalsToAcquire} Goals`;

  const activeIds = data.levelIds[Math.min(level - 1, data.levelIds.length - 1)] ?? [];
  const activePicks = picks.filter((p) => activeIds.includes(p));
  const steps = Object.values(log).reduce((a, v) => a + (Array.isArray(v) ? v.length : 0), 0);

  return {
    rung: engaged || secured ? rung : 'unlocked',
    days: qualDays.length,
    aims: aimWeeks.length,
    goals,
    todayActions,
    todayOrbDone,
    weekDays,
    blockAims,
    next,
    tierIdx,
    secured,
    pct,
    level,
    maxed,
    doneToday,
    activePicks,
    points: goals,
    steps,
    todayCount: qualDays.length,
    dayAwards: 0,
    levelFloor: (level - 1) * L.goalsToAcquire,
    levelTarget: level * L.goalsToAcquire,
    ratePerDay: 0,
    projectedDate: null,
    seasonEnd: null,
  };
}

/** Picks are open any day (no weekday lock). Kept as a named export for callers that gate on it. */
export function isPickDay(): boolean {
  return true;
}

/** The weekly pick is PROMPTED at the weekly boundary evening (ruling 1.11). */
export function isPickPrompt(game: GameSettings = DEFAULT_GAME_SETTINGS, now: Date = new Date()): boolean {
  return isWeekBoundaryEvening(now, game);
}

/** Local 'YYYY-MM-DD' for today (device local time). */
export function todayKey(): string {
  return dayKeyOf(new Date());
}

/** Human ladder summary for one deity, e.g. "Training · 2 of 3 Goals". */
export function ladderLabel(s: ProgressStats, game: GameSettings = DEFAULT_GAME_SETTINGS): string {
  if (s.secured) return 'Acquired';
  if (s.rung === 'unlocked') return 'Unlocked';
  return `Training · ${s.goals} of ${game.ladder.goalsToAcquire} Goals`;
}
